import React from "react";
import { Input, Select } from "src/components";
import countries from "src/utils/constants/countries/countries.json";
import { feedback } from "src/pages/LeanDeposit/tools/helper";
import { Grid, Cell } from "./style";
import { RequiredFieldsProvider } from "src/components/Form/requiredFields";
import { bankAccountSchema } from "./schema";

/**
 * The fields of a bank account — the only copy of them.
 *
 * This product's fields mark what is OPTIONAL rather than what is required,
 * so the three a bank may not have carry the note and the six it takes to
 * send money do not.
 *
 * Both dialogs that collect an account render this, so the Deposit page and
 * Settings ask the same nine questions, in the same order, with the same
 * labels and the same rules. See `./schema` for what the two forms used to
 * disagree about.
 *
 * The order is the order a person reads a bank's own wire form in: who the
 * account belongs to, then where the money lands, then which bank, then how
 * to route to it.
 */

type Props = {
  /** A formik bag. Both call sites already have one. */
  formik: any;
};

export const BankAccountForm = ({ formik }: Props) => {
  const field = (name: string) => ({
    name,
    id: name,
    size: "large" as const,
    error: feedback(formik, name),
    value: formik.values[name] ?? "",
    onChange: formik.handleChange,
    onBlur: () => formik.setFieldTouched(name, true),
  });

  return (
    /**
     * The schema, declared once.
     *
     * Every field below asks it whether it is required and marks its own
     * label, so the six that the submit will refuse when empty carry a `*`
     * and the three that are genuinely optional carry nothing — without a
     * single `required` prop to forget. That forgetting is why this product
     * kept shipping required fields with no mark at all.
     */
    <RequiredFieldsProvider schema={bankAccountSchema}>
      <Grid>
        {/* A row each for the long ones and a pair for the short ones. An IBAN
            runs to 34 characters, an account name and either address longer
            still — in a half-width cell they are read through a letterbox. */}
        <Cell $span={2}>
          <Input {...field("accountName")} label="Account name" />
        </Cell>

        <Cell $span={2}>
          <Input
            {...field("iban")}
            label="Account number / IBAN"
            placeholder="e.g. AE07 0331 2345 6789 0123 456"
          />
        </Cell>

        <Cell $span={2}>
          {/* It was "Address", between an IBAN and a bank name, where it could
              equally have meant the bank's. The bank's own is two fields down
              and says so. */}
          <Input {...field("address")} label="Account holder's address" />
        </Cell>

        <Cell>
          <Input {...field("bankName")} label="Bank name" />
        </Cell>
        <Cell>
          <Input {...field("branchCity")} label="Bank city" />
        </Cell>

        <Cell $span={2}>
          <Input {...field("branchLocation")} label="Bank address" />
        </Cell>

        <Cell>
          <Input
            {...field("bankNumber")}
            label="Swift code"
            placeholder="e.g. EBILAEAD"
          />
        </Cell>
        <Cell>
          {/* Optional: most banks outside the US have no ABA at all. */}
          <Input {...field("aba")} label="Routing / ABA / Fedwire code" />
        </Cell>

        <Cell $span={2}>
          <Select
            label="Remittance country"
            placeholder="Remittance country"
            id="remittanceCountry"
            name="remittanceCountry"
            items={countries}
            defaultValue={countries[0].value}
            value={formik.values.remittanceCountry}
            error={feedback(formik, "remittanceCountry")}
            size="large"
            onSelectionChange={(value: any, name: any) =>
              formik.setFieldValue(name, value)
            }
            onBlur={() => formik.setFieldTouched("remittanceCountry", true)}
          />
        </Cell>
      </Grid>
    </RequiredFieldsProvider>
  );
};

export default BankAccountForm;
